// The locked 11-question EA onboarding questionnaire.
//
// Source of truth: docs/DECISION_LOG.md, entry
// `DONNAP-REVIEW-AND-EA-ONBOARDING-LOCKED` (2026-08-21), approved by Nick
// verbatim ("Confirm."). The copy below is reproduced exactly as locked —
// this file is not the place to improve, shorten or rephrase it. If the
// wording ever needs to change, that is a new decision-log entry first.
//
// Field names match `backend/services/ea/contract.py`'s `ONBOARDING_FIELDS`
// exactly, in the same order. `purpose` and `personality` are the two new
// fields added alongside this build; the other nine already existed.
//
// WHY EACH QUESTION CARRIES A `type`
// ===================================
// Nine of the eleven are a single choice from a short fixed list — rendered
// as a `single-select` (a button group standing in for a radio group, styled
// like the rest of the v2 shell rather than native OS radio buttons).
// `working_hours` and `notification_preferences` are structured — real
// date/time and multi-select controls, not free text asking the operator to
// type a time range. `priorities` and `recurring_responsibilities` are open
// text, because there is no fixed vocabulary that would not put words in the
// operator's mouth.

export const FRAMING_COPY =
  "Before your EA goes live, a few quick questions so it works the way you " +
  "actually want — not the default. Takes about 3 minutes. You can change " +
  "any of this later in Settings.";

export const CLOSING_COPY =
  "Your EA is set up. It'll work the way you just described — you can " +
  "adjust any of this anytime in Settings → Customize EA.";

export const DAYS_OF_WEEK = [
  { id: "mon", label: "Mon" },
  { id: "tue", label: "Tue" },
  { id: "wed", label: "Wed" },
  { id: "thu", label: "Thu" },
  { id: "fri", label: "Fri" },
  { id: "sat", label: "Sat" },
  { id: "sun", label: "Sun" },
];

/** A short, dependable list. Used as a fallback when the runtime does not
 * support `Intl.supportedValuesOf("timeZone")` (older browsers/test envs). */
export const FALLBACK_TIMEZONES = [
  "America/Los_Angeles",
  "America/Denver",
  "America/Chicago",
  "America/New_York",
  "America/Anchorage",
  "Pacific/Honolulu",
  "UTC",
  "Europe/London",
  "Europe/Paris",
  "Europe/Berlin",
  "Asia/Kolkata",
  "Asia/Singapore",
  "Asia/Tokyo",
  "Australia/Sydney",
];

export function detectTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

export function listTimezones() {
  try {
    if (typeof Intl.supportedValuesOf === "function") {
      return Intl.supportedValuesOf("timeZone");
    }
  } catch {
    // fall through to the fallback list
  }
  return FALLBACK_TIMEZONES;
}

const SINGLE = "single-select";
const HOURS = "working-hours";
const NOTIF = "notification-preferences";
const TEXT_LIST = "open-text-list";
const TEXT = "open-text";

/**
 * The eleven questions, in the locked order. `field` is the exact
 * `ONBOARDING_FIELDS` name it writes.
 */
export const QUESTIONS = [
  {
    field: "purpose",
    n: 1,
    label: "Purpose",
    prompt: "What do you mainly want your EA for?",
    type: SINGLE,
    allowOther: true,
    options: [
      "Keeping me on top of the business — performance, decisions, what needs my attention",
      "Managing my day-to-day — schedule, follow-ups, staying organized",
      "Both, roughly equally",
      "Something else",
    ],
  },
  {
    field: "personality",
    n: 2,
    label: "Personality",
    prompt: "How should your EA come across?",
    type: SINGLE,
    options: [
      "A sharp chief of staff — efficient, no-nonsense, keeps things moving",
      "A trusted advisor — thoughtful, asks good questions, pushes back when needed",
      "A low-key assistant — quiet, stays out of the way, speaks only when useful",
    ],
  },
  {
    field: "communication_style",
    n: 3,
    label: "Communication style",
    prompt: "How should it talk to you?",
    type: SINGLE,
    options: [
      "Direct and brief — just the facts, minimal framing",
      "Warm and conversational — like talking to a person",
      "Formal and structured — clear sections, no small talk",
    ],
  },
  {
    field: "initiative",
    n: 4,
    label: "Initiative",
    prompt: "How much should it do on its own?",
    type: SINGLE,
    options: [
      "Wait until I ask — don't do anything unprompted",
      "Flag things it notices, but let me decide what to do",
      "Handle routine things on its own, and tell me after",
    ],
  },
  {
    field: "interruption_preferences",
    n: 5,
    label: "Interruption preferences",
    prompt: "When should it interrupt you?",
    type: SINGLE,
    options: [
      "Only for something genuinely urgent",
      "Time-sensitive things, during working hours only",
      "Anything it thinks is relevant — I'll sort through it myself",
    ],
  },
  {
    field: "briefing_format",
    n: 6,
    label: "Briefing format",
    prompt: "How should briefings look?",
    type: SINGLE,
    options: [
      "Short bullet points",
      "A brief written summary, a few sentences",
      "Just the raw numbers/facts, no narrative",
    ],
  },
  {
    field: "follow_up_persistence",
    n: 7,
    label: "Follow-up persistence",
    prompt: "How persistent should it be about something you haven't dealt with?",
    type: SINGLE,
    options: [
      "Mention it once, then drop it",
      "A couple of gentle reminders",
      "Keep raising it until I explicitly deal with it",
    ],
  },
  {
    field: "working_hours",
    n: 8,
    label: "Working hours",
    prompt: "When are you working?",
    type: HOURS,
  },
  {
    field: "notification_preferences",
    n: 9,
    label: "Notification preferences",
    prompt: "How should it reach you, and how often?",
    type: NOTIF,
    channelOptions: ["Telegram", "Email", "Both"],
    frequencyOptions: [
      "Immediately as things come up",
      "Once daily digest",
      "Only when something's urgent",
    ],
  },
  {
    field: "priorities",
    n: 10,
    label: "Priorities",
    prompt: "What should it keep an eye on for you? 1–3 short entries.",
    type: TEXT_LIST,
    max: 3,
    placeholder:
      "e.g. 'watching ABH compliance risk,' 'keeping me on top of underperforming campaigns,' " +
      "'protecting my time from low-value meetings'",
  },
  {
    field: "recurring_responsibilities",
    n: 11,
    label: "Recurring responsibilities",
    prompt: "Anything it should handle on a recurring schedule?",
    type: TEXT,
    optional: true,
    placeholder:
      "e.g. 'weekly performance digest every Monday,' 'monthly budget review,' " +
      "'check in on operator X's coaching progress'",
  },
];

/** What is sent for an optional field the operator left blank. Truthful —
 * it records that the question was asked and answered "none" — rather than
 * an empty value that would leave onboarding permanently incomplete. */
export const NO_RECURRING_RESPONSIBILITIES = "None specified.";
